import { Badge } from "@mantine/core"
import { IconEdit, IconRefresh, IconTrash } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Missing, Accordion, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { FindProdSiteFieldDefsByOrgIdResponse } from "@tfg/types/schemas/modules/traceability/dtos/find-prod-site-field-defs-by-org-id.dto"

interface Props {
  handleDelete: (id: string) => void
  handleUpdate: (
    productionSiteFieldDefinition: FindProdSiteFieldDefsByOrgIdResponse[number]
  ) => void
  handleAddToProducts: (id: string) => void
}

const columnHelper =
  createColumnHelper<FindProdSiteFieldDefsByOrgIdResponse[number]>()
export const useColumns = ({
  handleDelete,
  handleUpdate,
  handleAddToProducts,
}: Props) => {
  const { t } = useTranslation("admin")
  return useMemo<
    ColumnDef<FindProdSiteFieldDefsByOrgIdResponse[number], any>[]
  >(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("forActivities", {
        header: "Activities",
        cell: ({ getValue }) => (
          <Accordion w={400}>
            <Accordion.Item value="activities">
              <Accordion.Control>
                <TextCell
                  value={t("prodSiteFieldDefs.columns.activitiesValue", {
                    length: getValue().length,
                  })}
                />
              </Accordion.Control>
              <Accordion.Panel>
                {getValue().map((activityName: string) => (
                  <Badge key={activityName} c="gray">
                    {activityName}
                  </Badge>
                ))}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        ),
      }),
      columnHelper.accessor("enumValues", {
        header: "Enum Values",
        cell: ({ getValue }) => {
          const values = getValue()
          return values.length === 0 ? (
            <Missing />
          ) : (
            <Accordion w={400}>
              <Accordion.Item value="activities">
                <Accordion.Control>
                  <TextCell
                    value={t("prodSiteFieldDefs.columns.enumValues", {
                      length: getValue().length,
                    })}
                  />
                </Accordion.Control>
                <Accordion.Panel>
                  {getValue().map((activity: string) => (
                    <Badge
                      key={activity}
                      c="gray"
                      fz="var(--table-cell-text-size)"
                    >
                      {activity}
                    </Badge>
                  ))}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          )
        },
      }),
      columnHelper.accessor((row) => ({ min: row.min, max: row.max }), {
        header: "Min-Max",
        cell: ({ getValue }) => {
          const { min, max } = getValue()

          return min === null && max === null ? (
            <Missing />
          ) : (
            <TextCell value={`${min ?? ""}-${max ?? ""}`} />
          )
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-delete",
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconTrash />}
            onClick={() => handleDelete(row.original.id)}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-update",
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconEdit />}
            onClick={() => handleUpdate(row.original)}
          />
        ),
        enableSorting: false,
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-addToProducts",
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconRefresh />}
            onClick={() => handleAddToProducts(row.original.id)}
            toolTip="Apply this production site field definition to organization products"
          />
        ),
        enableSorting: false,
      }),
    ],
    [handleDelete, handleUpdate, handleAddToProducts, t]
  )
}
